import { Warehouse } from 'lucide-react';
import {
  WAREHOUSE_FIELD_LABELS,
  visibleInfoFields,
  type DealWarehouseInfo,
  type WarehouseFieldsConfig,
} from '../../../lib/deals/warehouseFields';

/**
 * Indicazioni per il magazzino, nella card Cliente sotto l'indirizzo di
 * consegna. Quali campi compaiono e quali sono obbligatori lo decide
 * l'organizzazione (Impostazioni → Offerte); l'obbligo si controlla al
 * salvataggio dell'offerta (DealForm).
 */
interface DealWarehouseFieldsProps {
  config: WarehouseFieldsConfig;
  value: DealWarehouseInfo | null | undefined;
  onChange: (value: DealWarehouseInfo) => void;
}

const controlCls =
  'min-w-0 flex-1 bg-transparent py-0.5 text-[11.5px] text-black/80 dark:text-white/80 border-0 border-b border-dashed border-black/15 dark:border-white/20 focus:outline-none focus:border-[#2383E2]';

export function DealWarehouseFields({ config, value, onChange }: DealWarehouseFieldsProps) {
  const fields = visibleInfoFields(config);
  if (fields.length === 0) return null;
  const v = value ?? {};
  const set = (key: keyof DealWarehouseInfo, next: string) => onChange({ ...v, [key]: next });

  return (
    <div className="pt-2 space-y-1">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-black/55 dark:text-white/55">
        <Warehouse className="h-3 w-3 flex-shrink-0 opacity-70" />
        Per il magazzino
      </div>
      {fields.map(key => {
        const required = config[key].mode === 'required';
        const label = (
          <span className="w-[130px] flex-shrink-0">
            {WAREHOUSE_FIELD_LABELS[key]}
            {required && <span className="text-red-600 dark:text-red-400"> *</span>}
          </span>
        );

        if (key === 'warehouse_notes') {
          return (
            <label key={key} className="flex items-start gap-1.5 text-[11.5px] text-black/65 dark:text-white/65">
              {label}
              <textarea
                rows={2}
                value={v.warehouse_notes ?? ''}
                onChange={e => set('warehouse_notes', e.target.value)}
                placeholder="Indicazioni per chi prepara l'ordine"
                className={`${controlCls} resize-y`}
              />
            </label>
          );
        }

        const current = v[key] ?? '';
        // Una voce tolta dalle impostazioni resta leggibile sulle offerte che la usano
        const options = current && !config[key].options.includes(current)
          ? [...config[key].options, current]
          : config[key].options;
        return (
          <label key={key} className="flex items-center gap-1.5 text-[11.5px] text-black/65 dark:text-white/65">
            {label}
            <select value={current} onChange={e => set(key, e.target.value)} className={`${controlCls} cursor-pointer`}>
              <option value="">{required ? 'Scegli…' : '—'}</option>
              {options.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </label>
        );
      })}
    </div>
  );
}

export default DealWarehouseFields;
